import { DateTime } from 'luxon';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import LinkInI18nBlock from 'src/enosikit/components/LinkInI18nBlock';
import { UNTRADED_ENERGY_KEY } from 'src/util/constants';
import {
  COLOR_EXPORT, COLOR_IMPORT,
  OPACITY_100, OPACITY_40, OPACITY_10,
  UNIT_CARBON, UNIT_CURRENCY, UNIT_ENERGY,
  carbonProps, currencyProps, energyProps, CARBON_EMISSION,
  GRAM, KILO_GRAM, TONNE, MEGA_TONNE, KILO_TONNE,
  COLOR_IMPORT_UNTRADED, COLOR_EXPORT_UNTRADED,
}
  from './chartConstants';

export const convertInt = (operation, value) => (operation === 'sell' ? -Math.abs(value) : value);

/**
 * Prepares the final data for the trade and meter chart
 * @param {Array} chartdata
 * @param {string} direction - import or export
 * @returns {Array} - chart data (meter and trade).
 */
export const formFinalChartData = (chartdata, direction) => {
  const finalChartData = [];
  if (chartdata) {
    chartdata.forEach((item) => {
      const { x, y } = item || {};
      const chartDataObj = {
        x: x?.ts,
        y: convertInt(direction, y),
        fullDateObj: x,
        label: '',
      };
      finalChartData.push(chartDataObj);
    });
  }

  return finalChartData;
};

export const chartStateOpacity = (key, hoverKeys, selectedKeys) => {
  if (hoverKeys.length === 0 && selectedKeys.length === 0) { return OPACITY_100; }
  if (hoverKeys.includes(key)) { return OPACITY_100; }
  if (selectedKeys.includes(key)) {
    return hoverKeys.length === 0 ? OPACITY_100 : OPACITY_40;
  }
  return OPACITY_10;
};

export const getEmptyDataMsg = () => {
  const intl = useIntl();
  return {
    meter: <FormattedMessage
      id="chart.components.chart_helpers.meter.missing_data"
      defaultMessage="No meter data available for the selected time range."
      values={{
        link: (chunks) => (
          <LinkInI18nBlock
            url={
              intl.formatMessage({
                id: 'chart.components.chart_helpers.meter.missing_data.url',
                defaultMessage: 'chart.components.chart_helpers.meter.missing_data.url',
              })
            }
            urlKey="chart.components.chart_helpers.meter.missing_data.url"
            chunks={chunks}
          />
        ),
      }}
    />,
    trade: <FormattedMessage
      id="chart.components.chart_helpers.trade.missing_data"
      defaultMessage="No trade data available for the selected time range."
      values={{
        link: (chunks) => (
          <LinkInI18nBlock
            url={
              intl.formatMessage({
                id: 'chart.components.chart_helpers.trade.missing_data.url',
                defaultMessage: 'chart.components.chart_helpers.trade.missing_data.url',
              })
            }
            urlKey="chart.components.chart_helpers.trade.missing_data.url"
            chunks={chunks}
          />
        ),
      }}
    />,
  };
};

export const showNoDataMsg = (chartType) => (
  <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
    <div className="alert alert-warning">
      {getEmptyDataMsg()[chartType]}
    </div>
  </div>
);

/**
 * Define the bar color
 * @param {number} yValue
 * @param {string} key - residual, community ...
 * @returns {string} - bar colour
 */
export const barColor = (yValue, key) => {
  if (!yValue && !key) { return 'none'; }

  const isUntraded = key === UNTRADED_ENERGY_KEY;

  const buyColor = isUntraded ? COLOR_IMPORT_UNTRADED : COLOR_IMPORT;
  const sellColor = isUntraded ? COLOR_EXPORT_UNTRADED : COLOR_EXPORT;

  return (yValue >= 0 ? buyColor : sellColor);
};

// if the x position is half way through the chart, flip the tooltip to the left
export const getTooltipPosition = (x, chartWidth) => {
  if (chartWidth / x < 2) {
    return x - 200;
  }
  return x;
};

export const carbonLabel = () => (
  <>
    CO
    <sub>2</sub>
    &bull;e
  </>
);

export const getUnitProps = (unit) => {
  switch (unit) {
    case UNIT_CARBON:
      return carbonProps;
    case UNIT_CURRENCY:
      return currencyProps;
    case UNIT_ENERGY:
      return energyProps;
    default:
      return energyProps;
  }
};

export const formatCarbonEmission = (value) => {
  if (!value) {
    return null;
  }
  const finalValues = { value };
  let formatLabel = KILO_GRAM;
  if (value < 1.0) {
    formatLabel = GRAM;
    finalValues.value = value * 1000;
  } else if (value >= 1.0 && value < 1000.0) {
    formatLabel = KILO_GRAM;
  } else if (value >= 1000.0 && value < 1000000.0) {
    formatLabel = TONNE;
    finalValues.value = value / 1000;
  } else if (value >= 1000000.0 && value < 1000000000.0) {
    formatLabel = KILO_TONNE;
    finalValues.value = value / 1000000;
  } else {
    formatLabel = MEGA_TONNE;
    finalValues.value = value / 1000000000;
  }
  finalValues.label = `${formatLabel} ${CARBON_EMISSION}`;
  return finalValues;
};

const formatMillisecond = 'SSS';
const formatSecond = ':ss';
const formatMinute = 'HH:mm';
const formatHour = 'h a';
const formatDay = 'ccc dd';
const formatWeek = 'LLL dd';
const formatMonth = 'LLLL';
const formatYear = 'yyyy';

/**
 * Formats the ticks that get rendered in the axis of the bar chart.
 * @param {string} zone - The time zone to use for formatting the ticks.
 * @returns {Function} - A function that formats the ticks in the bar chart axis.
 *   The returned function takes a tick as input and returns a formatted string.
 */
// eslint-disable-next-line arrow-body-style
export const handleTickFormat = (zone) => {
  /**
   * Formats a single tick value.
   * @param {string} tick - The tick to be formatted, provided as a JavaScript Date string.
   * @returns {string} - The formatted tick value.
   */
  return (tick) => {
    const timestamp = DateTime.fromMillis(tick.getTime()).setZone(zone);

    if (timestamp.startOf('second') < timestamp) {
      return timestamp.toFormat(formatMillisecond);
    }

    if (timestamp.startOf('minute') < timestamp) {
      return timestamp.toFormat(formatSecond);
    }

    if (timestamp.startOf('hour') < timestamp) {
      return timestamp.toFormat(formatMinute);
    }

    if (timestamp.startOf('day') < timestamp) {
      return timestamp.toFormat(formatHour);
    }

    if (timestamp.startOf('month') < timestamp) {
      if (timestamp.startOf('week') < timestamp) {
        return timestamp.toFormat(formatDay);
      }
      return timestamp.toFormat(formatWeek);
    }

    if (timestamp.startOf('year') < timestamp) {
      return timestamp.toFormat(formatMonth);
    }

    return timestamp.toFormat(formatYear);
  };
};
